import { useEffect, useMemo, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { View } from 'react-native';
import { Alert } from '@/common/components/Alert/Alert';
import { BodyText } from '@/common/components/BodyText/BodyText';
import { Button } from '@/common/components/Button/Button';
import { Card } from '@/common/components/Card/Card';
import { Dialog } from '@/common/components/Dialog/Dialog';
import { ControlledDateField } from '@/common/components/Fields/ControlledDateField/ControlledDateField';
import { ControlledSelectField } from '@/common/components/Fields/ControlledSelectField/ControlledSelectField';
import { ControlledTextField } from '@/common/components/Fields/ControlledTextField/ControlledTextField';
import { Grid } from '@/common/components/Grid/Grid';
import { Heading } from '@/common/components/Heading/Heading';
import { Icon } from '@/common/components/Icons/Icon';
import { Stack } from '@/common/components/Stack/Stack';
import { TextAction } from '@/common/components/TextAction/TextAction';
import { Translate } from '@/common/components/Translate/Translate';
import { determineKinships } from '@/common/constants/kinship';
import { TAddFamilyMembersFormValues } from '@/common/containers/Dashboard/Insurance/InsuranceAddMembersPage/components/AddMemberCardForm/AddMemberCardForm.schema';
import { AnalyticsService } from '@/common/integrations/analytics/analytics.service';
import { StringUtils } from '@/common/utils/string';
import { useMarket } from '@/web/hooks/useMarket';
import { genders } from '@coverflex-tech/hypernova-utils';
import { styles } from './AddMemberCardForm.styles';

export const AddMemberCardForm = ({
  member,
  disabled,
  onRemove,
  cardRef,
  memberIndex,
}: TAddMemberCardFormProps) => {
  const { t } = useTranslation();
  const { slug } = useMarket();
  const kinshipList = useMemo(() => determineKinships(slug), [slug]);
  const [showRemoveMemberModal, setShowRemoveMemberModal] = useState(false);
  const kinshipToString = kinshipList.map((kinship) => ({
    ...kinship,
    label: t(kinship.label),
  }));
  const genderToString = genders.map((gender) => ({
    ...gender,
    label: t(gender.label),
  }));

  const { control, setError } = useFormContext<TAddFamilyMembersFormValues>();

  const shortName = useWatch({
    name: `members.${memberIndex}.shortName`,
    control,
  });

  const fullName = useWatch({
    name: `members.${memberIndex}.fullName`,
    control,
  });

  useEffect(() => {
    if (member.error === 'tax_id_duplicated') {
      setError(`members.${memberIndex}.taxId`, {
        message: 'errors.api.duplicated_tax_id',
      });
    }
  }, [member.error, memberIndex, setError, t]);

  const alert =
    member.error === 'already_insured' ? (
      <Alert type="error" noBackground>
        <Translate
          color="systemError"
          i18nKey="dashboard.insurance_detail.add_family_members.error"
          components={{
            strong: <BodyText as="strong" bold color="systemError" size="sm" />,
            underline: (
              <BodyText
                as="strong"
                bold
                color="systemError"
                underline
                size="sm"
              />
            ),
            button: <TextAction onPress={() => AnalyticsService.openChat()} />,
          }}
        />
      </Alert>
    ) : member.error === 'already_exists' ? (
      <Alert type="warning" noBackground>
        {t('dashboard.insurance_detail.add_family_members.warning')}
      </Alert>
    ) : null;

  return (
    <>
      {showRemoveMemberModal && (
        <Dialog
          iconBadgeColor="accentSecondary20"
          icon={<Icon icon="family-pants" />}
          message={
            <Heading level={3}>
              {t(
                'dashboard.insurance_detail.simulate_family_modal.remove.message'
              )}
            </Heading>
          }
          primaryAction={{
            children: t(
              'dashboard.insurance_detail.simulate_family_modal.remove.btn'
            ),
            onPress: (close) => {
              onRemove(member);
              close();
            },
          }}
          secondaryAction={{
            children: t('general.navigation.cancel'),
          }}
          onClose={() => setShowRemoveMemberModal(false)}
        />
      )}
      <Card
        plain
        className={styles.host({
          error: member.error === 'already_insured',
          warning: member.error === 'already_exists',
        })}
        ref={cardRef}
      >
        <Stack gap="md">
          <Stack horizontal justify="between" align="center">
            <Heading level={4}>
              {shortName ||
                StringUtils.getFirstName(fullName) ||
                t(
                  'dashboard.insurance_detail.simulate_family_modal.new_family_member'
                )}
            </Heading>
            <Button
              variant="tertiary"
              icon={<Icon icon={['far', 'trash-can']} />}
              onPress={() => setShowRemoveMemberModal(true)}
              disabled={disabled}
            >
              {t('general.action.remove')}
            </Button>
          </Stack>
          <>
            <Grid gap="form" columnBasis="basis-[25rem]">
              <ControlledSelectField
                name={`members.${memberIndex}.type`}
                label={t(
                  'dashboard.insurance_detail.simulate_family_modal.kinship_degree'
                )}
                options={kinshipToString}
                disabled
              />
              <ControlledTextField
                name={`members.${memberIndex}.fullName`}
                label={t(
                  'dashboard.insurance_detail.simulate_family_modal.full_name'
                )}
                disabled={!member.isUserAdded}
              />
              <ControlledTextField
                name={`members.${memberIndex}.shortName`}
                label={t(
                  'dashboard.insurance_detail.simulate_family_modal.short_name'
                )}
                disabled
              />
              <ControlledTextField
                name={`members.${memberIndex}.taxId`}
                label={t(
                  'dashboard.insurance_detail.simulate_family_modal.tax_number'
                )}
                disabled={
                  !member.isUserAdded &&
                  !member.editableFields?.includes('taxId')
                }
              />
              <ControlledDateField
                name={`members.${memberIndex}.birthdate`}
                label={t(
                  'dashboard.insurance_detail.simulate_family_modal.birthdate'
                )}
                disabled
              />
              <ControlledSelectField
                name={`members.${memberIndex}.gender`}
                options={genderToString}
                label={t(
                  'dashboard.insurance_detail.simulate_family_modal.gender'
                )}
                disabled={
                  !member.isUserAdded &&
                  !member.editableFields?.includes('gender')
                }
              />
            </Grid>
          </>
          {alert}
        </Stack>
      </Card>
    </>
  );
};

export type TAddMemberCardFormProps = {
  member: TAddFamilyMembersFormValues['members'][number];
  disabled?: boolean;
  onRemove: (member: TAddFamilyMembersFormValues['members'][number]) => void;
  cardRef?: React.RefObject<View | null>;
  memberIndex: number;
};
